import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, AlertCircle, Eye, EyeOff } from 'lucide-react'
import { fetchBlogPost, createBlogPost, updateBlogPost } from '../utils/api'

const CATEGORIES = ['Guias', 'Automóvel', 'Habitação', 'Legislação', 'Dicas']

export function BlogEditorPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditing = Boolean(id) && id !== 'new'

  const [slug, setSlug] = useState('')
  const [title, setTitle] = useState('')
  const [excerpt, setExcerpt] = useState('')
  const [content, setContent] = useState('')
  const [category, setCategory] = useState('Guias')
  const [readTime, setReadTime] = useState('5 min')
  const [enabled, setEnabled] = useState(true)
  const [loading, setLoading] = useState(isEditing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isEditing && id) {
      fetchBlogPost(id)
        .then((post) => {
          setSlug(post.slug)
          setTitle(post.title)
          setExcerpt(post.excerpt)
          setContent(post.content)
          setCategory(post.category)
          setReadTime(post.read_time)
          setEnabled(post.enabled)
        })
        .catch(() => setError('Erro ao carregar o artigo'))
        .finally(() => setLoading(false))
    }
  }, [id, isEditing])

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
  }

  const handleTitleChange = (value: string) => {
    setTitle(value)
    if (!isEditing) {
      setSlug(generateSlug(value))
    }
  }

  const handleSave = async () => {
    if (!slug.trim()) {
      setError('O slug é obrigatório')
      return
    }
    if (!title.trim()) {
      setError('O título é obrigatório')
      return
    }
    if (!excerpt.trim()) {
      setError('O resumo é obrigatório')
      return
    }
    if (!content.trim()) {
      setError('O conteúdo é obrigatório')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const postData = {
        slug: slug.trim(),
        title: title.trim(),
        excerpt: excerpt.trim(),
        content: content.trim(),
        category,
        read_time: readTime,
        enabled,
      }

      if (isEditing && id) {
        await updateBlogPost(id, postData)
      } else {
        await createBlogPost(postData)
      }

      navigate('/admin/blog')
    } catch {
      setError('Erro ao guardar o artigo')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="flex-1 py-6 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/blog')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Editar Artigo' : 'Novo Artigo'}
          </h1>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="card p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Detalhes do Artigo</h2>
            <button
              type="button"
              onClick={() => setEnabled(!enabled)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                enabled
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {enabled ? 'Publicado' : 'Rascunho'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="title" className="label">Título *</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                className="input"
                placeholder="Como Resolver um Contrato de Seguro"
              />
            </div>
            <div>
              <label htmlFor="slug" className="label">Slug (URL) *</label>
              <input
                type="text"
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="input"
                placeholder="como-resolver-contrato-seguro"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="category" className="label">Categoria</label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="input"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="readTime" className="label">Tempo de Leitura</label>
              <input
                type="text"
                id="readTime"
                value={readTime}
                onChange={(e) => setReadTime(e.target.value)}
                className="input"
                placeholder="5 min"
              />
            </div>
          </div>

          <div>
            <label htmlFor="excerpt" className="label">Resumo/Excerto *</label>
            <textarea
              id="excerpt"
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              className="input min-h-[80px] resize-none"
              placeholder="Breve descrição do artigo que aparece na listagem..."
            />
          </div>

          <div>
            <label htmlFor="content" className="label">Conteúdo (HTML) *</label>
            <p className="text-sm text-gray-500 mb-2">
              Use HTML para formatar o conteúdo. Tags suportadas: h2, h3, p, ul, ol, li, strong, em, a, div, etc.
            </p>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="input min-h-[300px] font-mono text-sm resize-y"
              placeholder="<p class='lead'>Introdução do artigo...</p>

<h2>Secção 1</h2>
<p>Conteúdo da secção...</p>"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/admin/blog')}
            className="btn-secondary flex-1"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex-1 gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                A guardar...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Guardar Artigo
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
